import { motion } from "framer-motion";
import { Search, Sprout, Banknote, Landmark, Quote, Users, TrendingDown, Smartphone, Wifi } from "lucide-react";
import { LEADERS, MINISTRIES, PARTNERS, WHY_STATS } from "@/lib/about";

const PIPELINE = [
  { icon: Search, step: "01", title: "Identify", text: "AI-driven scouting (aiScout) and the Talanta Hela Digital Hub surface talent from schools, counties and tournaments across all 47 counties." },
  { icon: Sprout, step: "02", title: "Nurture", text: "Kenya Academy of Sports, TVETA-accredited training, federations and creative partners coach and professionalize that talent." },
  { icon: Banknote, step: "03", title: "Monetize", text: "Direct-to-fan content, tickets, merchandise, collectibles, sponsorship matchmaking and digital commerce — income, not just exposure." },
];

const STAT_ICONS = [Users, TrendingDown, Smartphone, Wifi];

export const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="relative bg-white py-20 sm:py-28 overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-talanta-gold/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* What it is */}
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-terracotta">About Talanta Hela</span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-talanta-green leading-tight">
              Kenya's Sports, Creative &amp; Enterprise IP Platform
            </h2>
            <p className="mt-5 text-talanta-charcoal/75 leading-relaxed">
              Talanta Hela is a Government of Kenya platform engineered to reshape the economic and social fabric of Kenyan
              youth. Strategically designed to tackle unemployment, income generation and restricted market access, it
              unlocks job opportunities, widens market access and encourages the rise of solopreneurs in the sports and
              creative sectors — a <em className="text-talanta-green font-semibold not-italic">"New Economy"</em> where
              passion becomes sustainable prosperity.
            </p>
            <div className="mt-6 rounded-2xl border-l-4 border-talanta-red bg-talanta-cream p-5">
              <Quote className="h-5 w-5 text-talanta-red/60" />
              <p className="mt-2 font-display font-semibold text-talanta-green">Our mission</p>
              <p className="mt-1 text-sm text-talanta-charcoal/75">
                To nurture talent, expand livelihoods and elevate Kenya's sporting and creative industries through robust
                policy, strategic investment and cross-sector partnerships — offering one accessible gateway for
                monetisation, skills growth and international competitiveness.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            {PIPELINE.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.step}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  className="flex gap-5 rounded-2xl border border-talanta-green/10 bg-talanta-cream p-5 hover:shadow-lg transition-shadow"
                >
                  <div className="h-12 w-12 shrink-0 rounded-xl bg-talanta-green text-white flex items-center justify-center">
                    <Icon className="h-5 w-5 text-talanta-gold" />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold tracking-widest text-talanta-terracotta">STEP {s.step}</div>
                    <h3 className="font-display font-bold text-lg text-talanta-green">{s.title}</h3>
                    <p className="mt-1 text-sm text-talanta-charcoal/70">{s.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Why it matters */}
        <div className="mt-20 rounded-3xl bg-talanta-green text-white p-8 sm:p-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-gold">Why it matters</span>
              <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl leading-tight">A young, connected nation that still needs jobs</h3>
              <p className="mt-3 text-sm text-white/65">
                Talanta Hela is built to be the go-to super app for Kenyan youth — not just offering services, but direct
                income-generation opportunities, aligned with the Bottom-Up Economic Transformation Agenda (BETA).
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {WHY_STATS.map((s, i) => {
                const Icon = STAT_ICONS[i];
                return (
                  <div key={s.label} data-testid="why-stat" className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <Icon className="h-4 w-4 text-talanta-gold" />
                    <div className="mt-3 font-display font-bold text-2xl sm:text-3xl text-talanta-gold">{s.value}</div>
                    <div className="mt-1 text-xs text-white/65 leading-snug">{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Leadership */}
        <div className="mt-20">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-terracotta">The people behind it</span>
          <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-talanta-green">Championed at Cabinet level</h3>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {LEADERS.map((l) => (
              <figure key={l.name} data-testid="leader-card" className="rounded-2xl border border-talanta-green/10 bg-talanta-cream p-6 sm:p-7 flex flex-col">
                <Quote className="h-6 w-6 text-talanta-red/50" />
                <blockquote className="mt-3 text-talanta-charcoal/80 leading-relaxed text-sm sm:text-base flex-1">“{l.quote}”</blockquote>
                <figcaption className="mt-5 flex items-center gap-4 pt-5 border-t border-talanta-green/10">
                  <div className="h-16 w-16 rounded-full bg-talanta-green text-talanta-gold font-display font-bold flex items-center justify-center shrink-0 overflow-hidden ring-2 ring-talanta-gold/40">
                    {l.photo ? <img src={l.photo} alt={l.name} data-testid="leader-photo" className="h-full w-full object-cover" /> : l.initials}
                  </div>
                  <div>
                    <div className="font-display font-bold text-talanta-green">{l.name}</div>
                    <div className="text-xs text-talanta-charcoal/60">{l.title}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        {/* Ministries */}
        <div className="mt-16">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-terracotta">The ministries behind it</span>
          <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-talanta-green">A whole-of-government programme</h3>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {MINISTRIES.map((m) => (
              <a
                key={m.name}
                href={m.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-institution-card"
                className="group rounded-2xl border border-talanta-green/10 bg-white p-5 hover:-translate-y-1 hover:shadow-xl transition-all"
              >
                <div className="h-11 w-11 rounded-xl bg-talanta-red/10 flex items-center justify-center group-hover:bg-talanta-red transition-colors">
                  <Landmark className="h-5 w-5 text-talanta-red group-hover:text-white transition-colors" />
                </div>
                <div className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-talanta-terracotta">{m.tag}</div>
                <h4 className="mt-1 font-display font-bold text-talanta-green leading-snug">{m.name}</h4>
                <p className="mt-2 text-sm text-talanta-charcoal/65">{m.role}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Partners */}
        <div className="mt-16">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-charcoal/45">Delivered with partners</div>
          <div data-testid="partners-strip" className="mt-4 flex flex-wrap gap-2">
            {PARTNERS.map((p) => (
              <span key={p} className="rounded-full border border-talanta-green/15 bg-talanta-cream px-3.5 py-1.5 text-xs font-semibold text-talanta-green">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
